/* eslint-disable import/no-cycle */
/* eslint-disable no-unused-vars */
import {createSlice} from '@reduxjs/toolkit';
import get from 'lodash.get';
import isEmpty from 'lodash.isempty';
import {flushTemplateCache} from '@components/AI/aiSlice';
import {
    FETCH_OPTIONS_STEP, PACKAGE_TYPE_NAME, TFA_TYPES
} from '@constants/index';
import {fetchAvatars} from '@utils/requests/recipients';
import {getErrorMessage, getPrefixedMessageKey} from '@utils/requests/error';
import {parseJWT} from '@utils/jwtUtils';
import {contactApi} from '../../../../api/contact/api';
import {accountUserApi} from '../../../../api/accountUser/api';
import {userApi} from '../../../../api/user/api';
import {esvApi} from '../../../../api/esv/api';
import {groupApi} from '../../../../api/group/api';
import {recipientApi} from '../../../../api/recipient/api';
import {attachmentApi} from '../../../../api/attachment/api';
import {getAllAttachmentUploads} from '@api/attachmentUpload/api';
import {transactionApi} from '../../../../api/transaction/api';
import {setTransaction, fetchApprovalsAndFields as fetchTransactionApprovalsAndFields} from '../../Transaction/transactionSlice';
import {setTemplate, fetchApprovalsAndFields as fetchTemplateApprovalsAndFields} from '../../Template/templateSlice';

const getContactToSuggestion = (contact) => ({
    id: contact.id,
    firstName: contact.first_name,
    lastName: contact.last_name,
    email: contact.email,
    company: contact.organisation,
    title: contact.title,
    phone: contact.phone
});

const getUserToSuggestion = (user) => ({
    id: user.id,
    firstName: user.first_name,
    lastName: user.last_name,
    email: user.email,
    company: user.organisation,
    title: user.title,
    phone: user.phone || ''
});

const getNormalizedEmail = (email) => (email || '').trim().toLowerCase();

const getMergedSuggestions = (contactSuggestions, userSuggestions) => {
    const userSuggestionByEmail = new Map(
        userSuggestions.map((suggestion) => [getNormalizedEmail(suggestion.email), suggestion])
    );
    const enrichedContactSuggestions = contactSuggestions.map((contact) => {
        const matchingUser = userSuggestionByEmail.get(getNormalizedEmail(contact.email));
        if (!matchingUser) return contact;
        return {
            ...contact,
            phone: contact.phone || matchingUser.phone || '',
            company: contact.company || matchingUser.company || '',
            title: contact.title || matchingUser.title || ''
        };
    });
    const contactEmails = new Set(contactSuggestions.map(({email}) => getNormalizedEmail(email)));
    const uniqueUserSuggestions = userSuggestions.filter(({email}) => !contactEmails.has(getNormalizedEmail(email)));

    return [...enrichedContactSuggestions, ...uniqueUserSuggestions];
};

const parseUserIds = (raw) => {
    if (Array.isArray(raw)) return raw.filter(Boolean);
    if (typeof raw === 'string' && raw) return raw.split(',').map((id) => id.trim()).filter(Boolean);
    return [];
};

let cachedAccountUserIds = null;
let cachedAccountIdForUserIds = null;
let cachedAccountUserIdsPromise = null;

const getAccountUserIds = async (accountId) => {
    if (cachedAccountIdForUserIds === accountId && cachedAccountUserIds) {
        return cachedAccountUserIds;
    }
    if (cachedAccountIdForUserIds === accountId && cachedAccountUserIdsPromise) {
        return cachedAccountUserIdsPromise;
    }
    cachedAccountIdForUserIds = accountId;
    const localAccountId = accountId;
    cachedAccountUserIdsPromise = (async () => {
        const idsResponse = await accountUserApi.getActiveUserIds({accountId});
        const userIds = parseUserIds(idsResponse?.data?.user_ids || []);
        const result = new Set(userIds);
        if (cachedAccountIdForUserIds === localAccountId) {
            cachedAccountUserIds = result;
            cachedAccountUserIdsPromise = null;
        }
        return result;
    })();
    return cachedAccountUserIdsPromise;
};

const searchAccountUsers = async (accountId, search, perPage) => {
    try {
        const trimmedSearch = (search || '').trim();
        if (!trimmedSearch) return [];
        const [accountUserIds, usersResponse] = await Promise.all([
            getAccountUserIds(accountId),
            userApi.getUsers({params: {search: trimmedSearch, per_page: 100}})
        ]);
        const users = get(usersResponse, 'data', []);
        return users.filter((user) => accountUserIds.has(user.id)).slice(0, perPage);
    } catch {
        return [];
    }
};

/**
 * External contacts (customer relations) are gated by the `enableRecipientHistory`
 * account feature: when it is off the backend answers the contacts endpoint with 403.
 * That must not remove the colleague suggestions, so this failure is isolated to an
 * empty list rather than being awaited together with searchAccountUsers in a Promise.all
 * that would reject on either source.
 */
const searchContactSuggestions = async (user, search, perPage) => {
    try {
        const accountUserResponse = await accountUserApi.getAccountUserByAccountAndUser({
            accountId: user.account.id,
            userId: user.user.id
        });
        const accountUserId = accountUserResponse.data.id;
        const contactsResult = await contactApi.searchContacts(accountUserId, {search, per_page: perPage});
        return get(contactsResult, 'data', []).map(getContactToSuggestion);
    } catch {
        return [];
    }
};

const VERIFICATION_TIMEOUT_MS = 3000;

const getPackageStatePath = (packageType) => {
    if (packageType === PACKAGE_TYPE_NAME.TEMPLATE) return 'template.template';
    if (packageType === PACKAGE_TYPE_NAME.TRANSACTION) return 'transaction.transaction';
};

const dispatchFetchApprovalsAndFields = (dispatch, packageType, packageId) => {
    if (packageType === PACKAGE_TYPE_NAME.TEMPLATE) {
        dispatch(fetchTemplateApprovalsAndFields(packageId));
    } else {
        dispatch(fetchTransactionApprovalsAndFields(packageId));
    }
};

const APPROVALS_EXTRACTION_RETRY_DELAYS_MS = [800, 1600, 2800];

const getTotalApprovalsCount = (state, packageType) => {
    const statePath = packageType === PACKAGE_TYPE_NAME.TEMPLATE ? 'template.approvals' : 'transaction.approvals';
    const approvalsByDocumentId = get(state, statePath, {});
    return Object.values(approvalsByDocumentId).reduce((total, list) => total + (list?.length || 0), 0);
};

/**
 * After a new recipient is created the backend extracts that recipient's text-tag
 * approvals asynchronously, so the immediate refetch can miss them. This refetches
 * once, then retries a bounded number of times with increasing delays, stopping early
 * as soon as new approvals appear.
 */
const refetchApprovalsAndFieldsUntilExtracted = async (dispatch, getState, packageType, packageId) => {
    const fetchApprovals = packageType === PACKAGE_TYPE_NAME.TEMPLATE
        ? fetchTemplateApprovalsAndFields
        : fetchTransactionApprovalsAndFields;

    await dispatch(fetchApprovals(packageId));
    const initialCount = getTotalApprovalsCount(getState(), packageType);

    for (const delay of APPROVALS_EXTRACTION_RETRY_DELAYS_MS) {
        await new Promise((resolve) => setTimeout(resolve, delay));
        await dispatch(fetchApprovals(packageId));
        if (getTotalApprovalsCount(getState(), packageType) > initialCount) {
            break;
        }
    }
};

export const recipientsSlice = createSlice({
    name: 'recipients',
    initialState: {
        suggestions: [],
        updatedSuggestions: {
            name: {
                results: [],
                count: null,
                search: ''
            }
        },
        groups: [],
        verificationTypes: [],
        verifications: {},
        recipientAttachments: {},
        isFetchingSuggestions: false,
        fetchSuggestionsError: null,
        isUpdatingRoles: false,
        updateRolesError: null,
        isUpdatingFlow: false,
        updateFlowError: null,
        isUpdatingRole: false,
        updateRoleError: null,
        isFetchingGroups: false,
        fetchGroupsError: null,
        isDeletingRole: false,
        deleteRoleError: null,
        isVerificationTypesFetching: false,
        verificationTypesFetchError: null,
        sVerificationTypeFetching: false,
        updateSettingsError: null,
        verificationTypeFetchError: null,
        isVerificationTypeSaving: false,
        verificationTypeSaveError: null,
        avatars: [],
        fetchingVerificationRecipientsIds: [], // ids of recipients for which verification is being fetched
        groupMembersByGroupId: {},
        inFlightGroupMemberIds: [],
        currentUserGroupIdsByAccountId: {},
        inFlightGroupMembershipAccountIds: []
    },
    reducers: {
        setSuggestions: (state, action) => {
            state.suggestions = action.payload;
        },
        setUpdatedSuggestions: (state, {payload}) => {
            state.updatedSuggestions = {
                ...state.updatedSuggestions,
                [payload.name]: {
                    results: get(payload, 'data.results', []),
                    count: get(payload, 'data.count', []),
                    search: get(payload, 'search', '')
                }
            };
        },
        setIsFetchingSuggestions: (state, action) => {
            state.isFetchingSuggestions = action.payload;
        },
        setFetchSuggestionsError: (state, action) => {
            state.fetchSuggestionsError = action.payload;
        },
        setIsUpdatingRoles: (state, action) => {
            state.isUpdatingRole = action.payload;
        },
        setUpdateRolesError: (state, action) => {
            state.updateRoleError = action.payload;
        },
        setIsUpdatingFlow: (state, action) => {
            state.isUpdatingFlow = action.payload;
        },
        setUpdateFlowError: (state, action) => {
            state.updateFlowError = action.payload;
        },
        setIsUpdatingRole: (state, action) => {
            state.isUpdatingRole = action.payload;
        },
        setUpdateRoleError: (state, action) => {
            state.updateRoleError = action.payload;
        },
        setGroups: (state, action) => {
            state.groups = action.payload;
        },
        setGroupMembersByGroupId: (state, action) => {
            state.groupMembersByGroupId = {
                ...state.groupMembersByGroupId,
                ...action.payload
            };
        },
        setCurrentUserGroupIdsByAccountId: (state, action) => {
            state.currentUserGroupIdsByAccountId = {
                ...state.currentUserGroupIdsByAccountId,
                ...action.payload
            };
        },
        addInFlightGroupMembershipAccountIds: (state, action) => {
            const existing = new Set(state.inFlightGroupMembershipAccountIds);
            action.payload.forEach((accountId) => existing.add(accountId));
            state.inFlightGroupMembershipAccountIds = Array.from(existing);
        },
        removeInFlightGroupMembershipAccountIds: (state, action) => {
            state.inFlightGroupMembershipAccountIds = state.inFlightGroupMembershipAccountIds
                .filter((accountId) => !action.payload.includes(accountId));
        },
        addInFlightGroupMemberIds: (state, action) => {
            const existing = new Set(state.inFlightGroupMemberIds);
            action.payload.forEach((id) => existing.add(id));
            state.inFlightGroupMemberIds = Array.from(existing);
        },
        removeInFlightGroupMemberIds: (state, action) => {
            const removed = new Set(action.payload);
            state.inFlightGroupMemberIds = state.inFlightGroupMemberIds
                .filter((id) => !removed.has(id));
        },
        setIsFetchingGroups: (state, action) => {
            state.isFetchingGroups = action.payload;
        },
        setFetchGroupsError: (state, action) => {
            state.fetchGroupsError = action.payload;
        },
        setIsDeletingRole: (state, action) => {
            state.isDeletingRole = action.payload;
        },
        setDeleteRoleError: (state, action) => {
            state.deleteRoleError = action.payload;
        },
        setVerificationTypes: (state, action) => {
            state.verificationTypes = action.payload;
        },
        setVerifications: (state, action) => {
            const {
                packageId,
                recipientId,
                verification
            } = action.payload;
            const packageVerifications = get(state, `verifications[${packageId}]`, {});

            state.verifications = {
                ...state.verifications,
                [packageId]: {
                    ...packageVerifications,
                    [recipientId]: verification ? {...verification} : null
                }
            };
        },
        setIsVerificationTypesFetching: (state, action) => {
            state.isVerificationTypesFetching = action.payload;
        },
        setVerificationTypesFetchError: (state, action) => {
            state.verificationTypesFetchError = action.payload;
        },
        setUpdateSettingsError: (state, action) => {
            state.updateSettingsError = action.payload;
        },
        setIsVerificationTypeFetching: (state, action) => {
            state.isVerificationTypeFetching = action.payload;
        },
        setVerificationTypeFetchError: (state, action) => {
            state.verificationTypeFetchError = action.payload;
        },
        setIsVerificationTypeSaving: (state, action) => {
            state.isVerificationTypeSaving = action.payload;
        },
        setVerificationTypeSaveError: (state, action) => {
            state.verificationTypeSaveError = action.payload;
        },
        setAvatars: (state, action) => {
            state.avatars = [
                ...state.avatars,
                ...action.payload
            ];
        },
        addFetchingVerificationRecipientsIds: (state, action) => {
            state.fetchingVerificationRecipientsIds = [
                ...state.fetchingVerificationRecipientsIds,
                action.payload
            ];
        },
        removeFetchingVerificationRecipientsIds: (state, action) => {
            state.fetchingVerificationRecipientsIds = state.fetchingVerificationRecipientsIds
                .filter((id) => id !== action.payload);
        },
        setRecipientAttachments: (state, action) => {
            const {recipientId, attachments} = action.payload;
            state.recipientAttachments[recipientId] = attachments;
        },
        removeRecipientAttachments: (state, action) => {
            delete state.recipientAttachments[action.payload];
        }
    }
});

export const {
    setSuggestions,
    setUpdatedSuggestions,
    setGroups,
    setGroupMembersByGroupId,
    setCurrentUserGroupIdsByAccountId,
    addInFlightGroupMembershipAccountIds,
    removeInFlightGroupMembershipAccountIds,
    addInFlightGroupMemberIds,
    removeInFlightGroupMemberIds,
    setIsFetchingSuggestions,
    setFetchSuggestionsError,
    setIsUpdatingRoles,
    setUpdateRolesError,
    setIsUpdatingFlow,
    setUpdateFlowError,
    setIsUpdatingRole,
    setUpdateRoleError,
    setIsFetchingGroups,
    setFetchGroupsError,
    setIsDeletingRole,
    setDeleteRoleError,
    setVerificationTypes,
    setVerifications,
    setIsVerificationTypesFetching,
    setVerificationTypesFetchError,
    setUpdateSettingsError,
    setIsVerificationTypeFetching,
    setVerificationTypeFetchError,
    setIsVerificationTypeSaving,
    setVerificationTypeSaveError,
    setAvatars,
    addFetchingVerificationRecipientsIds,
    removeFetchingVerificationRecipientsIds,
    setRecipientAttachments,
    removeRecipientAttachments
} = recipientsSlice.actions;

const inFlightRecipientAttachments = new Map();

export const fetchRecipientAttachments = ({
    transactionId, recipientId
}) => async (dispatch) => {
    const requestKey = `${transactionId}:${recipientId}`;
    const inFlightRequest = inFlightRecipientAttachments.get(requestKey);
    if (inFlightRequest) {
        return inFlightRequest;
    }

    const request = (async () => {
        try {
            const result = await attachmentApi.getAttachmentsByRecipientId(transactionId, recipientId);
            const attachments = result?.data || [];
            const attachmentsWithFiles = await Promise.all(attachments.map(async (attachment) => {
                const files = await getAllAttachmentUploads(transactionId, attachment.id).catch(() => []);
                return {...attachment, files: Array.isArray(files) ? files : []};
            }));
            dispatch(setRecipientAttachments({recipientId, attachments: attachmentsWithFiles}));
            return attachmentsWithFiles;
        } catch {
            return [];
        }
    })();

    inFlightRecipientAttachments.set(requestKey, request);
    try {
        return await request;
    } finally {
        inFlightRecipientAttachments.delete(requestKey);
    }
};

export const selectRecipientAttachments = (recipientId) => (state) => state.recipients.recipientAttachments[recipientId] ?? null;

export const selectAllRecipientAttachments = (state) => state.recipients.recipientAttachments;

export const saveRecipientAttachments = ({
    transactionId, recipientId, attachments
}) => async (dispatch, getState) => {
    const current = selectRecipientAttachments(recipientId)(getState()) || [];
    await executeAttachmentDiff(transactionId, recipientId, current, attachments);
    await dispatch(fetchRecipientAttachments({transactionId, recipientId}));
};

export const resetRecipientErrors = () => async (dispatch) => {
    dispatch(setFetchSuggestionsError(null));
    dispatch(setUpdateRolesError(null));
    dispatch(setUpdateFlowError(null));
    dispatch(setUpdateRoleError(null));
    dispatch(setFetchGroupsError(null));
    dispatch(setDeleteRoleError(null));
    dispatch(setVerificationTypesFetchError(null));
    dispatch(setUpdateSettingsError(null));
    dispatch(setVerificationTypeFetchError(null));
    dispatch(setVerificationTypeSaveError(null));
};

export const fetchSuggestions = (
    search = ''
) => async (dispatch, getState) => {
    dispatch(setSuggestions([]));
    dispatch(setIsFetchingSuggestions(true));
    dispatch(setFetchSuggestionsError(null));

    try {
        const {user} = getState();
        const [contactSuggestions, accountUsers] = await Promise.all([
            searchContactSuggestions(user, search, 10),
            searchAccountUsers(user.account.id, search, 10)
        ]);
        const userSuggestions = accountUsers.map(getUserToSuggestion);
        const suggestions = getMergedSuggestions(contactSuggestions, userSuggestions);
        dispatch(setSuggestions(suggestions));
        dispatch(setIsFetchingSuggestions(false));
        return true;
    } catch (err) {
        dispatch(setFetchSuggestionsError(getErrorMessage(err)));
        dispatch(setIsFetchingSuggestions(false));
        return false;
    }
};

export const fetchUpdatedSuggestions = ({name, params}) => async (dispatch, getState) => {
    const {
        search = ''
    } = params;
    dispatch(setIsFetchingSuggestions(true));
    dispatch(setFetchSuggestionsError(null));

    try {
        const {user} = getState();
        const [contactSuggestions, accountUsers] = await Promise.all([
            searchContactSuggestions(user, search, FETCH_OPTIONS_STEP),
            searchAccountUsers(user.account.id, search, FETCH_OPTIONS_STEP)
        ]);
        const userSuggestions = accountUsers.map(getUserToSuggestion);
        const suggestions = getMergedSuggestions(contactSuggestions, userSuggestions);
        dispatch(setUpdatedSuggestions({
            data: {
                results: suggestions,
                count: suggestions.length
            },
            search,
            name
        }));
        dispatch(setIsFetchingSuggestions(false));
        return true;
    } catch (err) {
        const messageKey = getPrefixedMessageKey(get(err, 'response.data.messageKey', 'error_500.oops_broken'));
        dispatch(setFetchSuggestionsError(messageKey));
        dispatch(setIsFetchingSuggestions(false));
        return false;
    }
};

export const saveContact = ({
    email,
    firstName,
    lastName,
    organisation,
    title,
    phone
}) => async (_, getState) => {
    if (!email || !firstName || !lastName) {
        return false;
    }

    try {
        const {user} = getState();
        const accountUserResponse = await accountUserApi.getAccountUserByAccountAndUser({
            accountId: user.account.id,
            userId: user.user.id
        });
        const accountUserId = accountUserResponse.data.id;

        // Check if contact already exists by searching for email
        const existingContacts = await contactApi.searchContacts(accountUserId, {
            search: email,
            per_page: 10
        });
        const existingContact = get(existingContacts, 'data', [])
            .find((contact) => contact.email.toLowerCase() === email.toLowerCase());

        const optionalFields = {};
        if (organisation) {
            optionalFields.organisation = organisation;
        }
        if (title) {
            optionalFields.title = title;
        }
        if (phone) {
            optionalFields.phone = phone;
        }

        if (existingContact) {
            await contactApi.updateContact(accountUserId, existingContact.id, {
                first_name: firstName,
                last_name: lastName,
                ...optionalFields
            });
        } else {
            await contactApi.createContact(accountUserId, {
                email,
                first_name: firstName,
                last_name: lastName,
                ...optionalFields
            });
        }

        return true;
    } catch (err) {
        // Silently fail - saving contact should not block the main flow
        console.error('Failed to save contact:', err);
        return false;
    }
};

export const updateRoles = ({
    roles, isSigningFlow, packageType, setRoles
}) => async (dispatch, getState) => {
    const packageData = get(getState(), getPackageStatePath(packageType));

    if (isSigningFlow) {
        dispatch(setIsUpdatingFlow(true));
        dispatch(setUpdateFlowError(null));
    } else {
        dispatch(setIsUpdatingRoles(true));
        dispatch(setUpdateRolesError(null));
    }

    try {
        const payload = {
            recipients: roles
                .filter((r) => r.id)
                .map((r) => ({id: r.id, order: Math.max(0, r.order ?? 0)}))
        };
        const result = await recipientApi.reorderRecipients(packageData.id, payload);
        if (setRoles && Array.isArray(result?.data)) {
            dispatch(setRoles(result.data));
        }
        if (isSigningFlow) {
            dispatch(setIsUpdatingFlow(false));
        } else {
            dispatch(setIsUpdatingRoles(false));
        }
        return result.data;
    } catch (err) {
        if (isSigningFlow) {
            dispatch(setIsUpdatingFlow(false));
            dispatch(setUpdateFlowError(getErrorMessage(err)));
        } else {
            dispatch(setIsUpdatingRoles(false));
            dispatch(setUpdateRolesError(getErrorMessage(err)));
        }
        return false;
    }
};
function getSmsTfaPayload(tfa) {
    const rawPhone = tfa.challenges?.[0]?.phone || '';
    return {
        type: TFA_TYPES.SMS,
        phone: rawPhone.replace(/[^\d+]/g, '')
    };
}

function getQaTfaPayload(tfa) {
    return {
        type: TFA_TYPES.QA,
        questions: (tfa.challenges || []).map((c) => ({
            type: 'string',
            security_level: 'strict',
            question: c.question,
            answer: c.answer,
            mask_answers: c.maskInput ?? c.mask_answers ?? true
        }))
    };
}

function getSsoTfaPayload(tfa) {
    return {
        type: TFA_TYPES.SSO,
        alias: tfa.alias || get(tfa, 'challenges[0].alias', '')
    };
}

function getTfaPayload(tfa) {
    if (!tfa?.type) {
        return {type: TFA_TYPES.NONE};
    }
    if (tfa.type === TFA_TYPES.SMS) {
        return getSmsTfaPayload(tfa);
    }
    if (tfa.type === TFA_TYPES.QA) {
        return getQaTfaPayload(tfa);
    }
    if (tfa.type === TFA_TYPES.SSO) {
        return getSsoTfaPayload(tfa);
    }
    return {type: TFA_TYPES.NONE};
}

function getRequiredStringFields(formRole) {
    const fields = {};
    if (formRole.first_name !== undefined) {
        fields.first_name = formRole.first_name ?? '';
    }
    if (formRole.last_name !== undefined) {
        fields.last_name = formRole.last_name ?? '';
    }
    if (formRole.email !== undefined) {
        fields.email = formRole.email ?? '';
    }
    return fields;
}

function getOptionalFields(formRole, initialRole) {
    const fields = {};
    if (formRole.organisation !== undefined) {
        fields.organisation = formRole.organisation;
    }
    if (formRole.title !== undefined) {
        fields.title = formRole.title;
    }
    if (formRole.language) {
        fields.language = formRole.language;
    }
    if (formRole.personal_message) {
        fields.personal_message = formRole.personal_message;
    }
    const tfa = formRole.two_factor_authentication;
    const initialTfa = initialRole?.two_factor_authentication;
    if (tfa?.type) {
        fields.authentication = getTfaPayload(tfa);
    } else if (initialTfa?.type) {
        fields.authentication = {type: TFA_TYPES.NONE};
    }
    if (formRole.group_id) {
        fields.group_id = formRole.group_id;
    }
    return fields;
}

function buildRecipientPayload(formRole, initialRole) {
    return {
        order: formRole.order,
        placeholder: formRole.placeholder,
        change_recipient: formRole.change_recipient,
        signed_documents_in_mail: formRole.signed_documents_in_mail,
        ...getRequiredStringFields(formRole),
        ...getOptionalFields(formRole, initialRole)
    };
}

const ATTACHMENT_REJECTED_STATUS = 'rejected';

function getAttachmentCreatePayload(attachment) {
    return {
        name: attachment.name,
        description: attachment.description || '',
        is_required: attachment.is_required ?? false
    };
}

function getAttachmentUpdatePayload(attachment) {
    const payload = {
        name: attachment.name
    };
    if (attachment.description !== undefined) {
        payload.description = attachment.description;
    }
    if (attachment.is_required !== undefined) {
        payload.is_required = attachment.is_required;
    }
    return payload;
}

function getHasMetadataChanged(current, desired) {
    return current.name !== desired.name
        || (current.description ?? '') !== (desired.description ?? '')
        || Boolean(current.is_required) !== Boolean(desired.is_required);
}

function getHasValidName(attachment) {
    return Boolean(attachment?.name && String(attachment.name).trim());
}

/**
 * Diffs current vs desired attachments and persists the changes.
 * Inserts run sequentially so the backend preserves the recipient's attachment order (BO-755).
 */
async function executeAttachmentDiff(transactionId, recipientId, currentAttachments, desiredAttachments) {
    const current = currentAttachments || [];
    const desired = desiredAttachments || [];

    const toDelete = current.filter((c) => !desired.find((d) => d.id === c.id));
    const toInsert = desired.filter((d) => getHasValidName(d) && (!d.id || !current.find((c) => c.id === d.id)));
    const toUpdate = desired.filter((d) => {
        if (!d.id || !getHasValidName(d)) return false;
        const old = current.find((c) => c.id === d.id);
        return old && getHasMetadataChanged(old, d);
    });
    const toReject = desired.filter((d) => {
        if (d.status !== ATTACHMENT_REJECTED_STATUS) return false;
        const old = current.find((c) => c.id === d.id);
        return old && old.status !== ATTACHMENT_REJECTED_STATUS;
    });

    await Promise.allSettled(toDelete.map((a) => attachmentApi.deleteAttachment(transactionId, a.id)));
    await Promise.allSettled(toUpdate.map((a) => attachmentApi.updateAttachment(transactionId, a.id, getAttachmentUpdatePayload(a))));
    for (const attachment of toInsert) {
        // eslint-disable-next-line no-await-in-loop
        await attachmentApi.createAttachment(transactionId, recipientId, getAttachmentCreatePayload(attachment)).catch(() => {});
    }
    await Promise.allSettled(toReject.map((a) => attachmentApi.rejectAttachment(transactionId, a.id, {reject_reason: a.reject_reason || 'Rejected'})));
}

function getHasAnyRequiredAttachment(recipientAttachments) {
    return Object.values(recipientAttachments || {})
        .some((arr) => Array.isArray(arr) && arr.some((a) => a.is_required));
}

const enableReviewIfFirstRequiredAttachment = ({
    packageType, hadRequiredBefore
}) => async (dispatch, getState) => {
    if (hadRequiredBefore) return;

    const stateAfter = getState();
    const hasRequiredNow = getHasAnyRequiredAttachment(stateAfter.recipients?.recipientAttachments);
    if (!hasRequiredNow) return;

    const packageData = get(stateAfter, getPackageStatePath(packageType));
    if (!packageData?.id || packageData.autocomplete === false) return;

    const accountId = get(stateAfter, 'user.account.id');
    const userId = get(stateAfter, 'user.user.id');
    try {
        const isTemplate = packageType === PACKAGE_TYPE_NAME.TEMPLATE;
        const result = isTemplate
            ? await transactionApi.updateTemplate({
                accountId,
                userId,
                templateId: packageData.id,
                data: {autocomplete: false}
            })
            : await transactionApi.updateTransaction({
                accountId,
                userId,
                transactionId: packageData.id,
                data: {autocomplete: false}
            });
        const updated = {...packageData, ...result.data};
        if (isTemplate) {
            dispatch(setTemplate(updated));
        } else {
            dispatch(setTransaction(updated));
        }
    } catch {}
};

export const updateRole = ({
    recipient, initialRecipient, packageType
}) => async (dispatch, getState) => {
    const packageData = get(getState(), getPackageStatePath(packageType));
    const hadRequiredBefore = getHasAnyRequiredAttachment(getState().recipients?.recipientAttachments);
    dispatch(setIsUpdatingRole(true));
    dispatch(setUpdateRoleError(null));

    try {
        const payload = buildRecipientPayload(recipient, initialRecipient);
        let result;
        if (recipient.id) {
            result = await recipientApi.updateRecipient(packageData.id, recipient.id, payload);
            if (recipient.attachmentRequirements) {
                const currentResult = await attachmentApi.getAttachmentsByRecipientId(packageData.id, recipient.id);
                await executeAttachmentDiff(
                    packageData.id, recipient.id,
                    get(currentResult, 'data', []),
                    recipient.attachmentRequirements
                );
                await dispatch(fetchRecipientAttachments({transactionId: packageData.id, recipientId: recipient.id}));
            }
        } else {
            result = await recipientApi.createRecipient(packageData.id, payload);
            const recipientId = result.data.id;
            if (recipient.attachmentRequirements?.length) {
                const validAttachments = recipient.attachmentRequirements.filter((att) => getHasValidName(att));
                for (const attachment of validAttachments) {
                    // eslint-disable-next-line no-await-in-loop
                    await attachmentApi.createAttachment(packageData.id, recipientId, getAttachmentCreatePayload(attachment));
                }
                await dispatch(fetchRecipientAttachments({transactionId: packageData.id, recipientId}));
            }
        }

        await dispatch(enableReviewIfFirstRequiredAttachment({packageType, hadRequiredBefore}));

        if (packageType === PACKAGE_TYPE_NAME.TEMPLATE) {
            dispatch(flushTemplateCache({templateId: packageData.id}));
        }

        if (recipient.id) {
            dispatchFetchApprovalsAndFields(dispatch, packageType, packageData.id);
        } else {
            refetchApprovalsAndFieldsUntilExtracted(dispatch, getState, packageType, packageData.id).catch(() => {});
        }
        dispatch(setIsUpdatingRole(false));
        return result.data;
    } catch (err) {
        dispatch(setUpdateRoleError(getErrorMessage(err)));
        dispatch(setIsUpdatingRole(false));
        return false;
    }
};

export const deleteRole = ({
    recipientId, packageType
}) => async (dispatch, getState) => {
    const packageData = get(getState(), getPackageStatePath(packageType));

    dispatch(setIsDeletingRole(true));

    try {
        await recipientApi.deleteRecipient(packageData.id, recipientId);
        dispatch(removeRecipientAttachments(recipientId));
        if (packageType === PACKAGE_TYPE_NAME.TEMPLATE) {
            dispatch(flushTemplateCache({templateId: packageData.id}));
        }
        dispatchFetchApprovalsAndFields(dispatch, packageType, packageData.id);
        dispatch(setIsDeletingRole(false));
        return true;
    } catch (err) {
        dispatch(setIsDeletingRole(false));
        dispatch(setUpdateRoleError(getErrorMessage(err)));
        return false;
    }
};

export const fetchGroupsSummary = ({
    headers, baseUrl, options = {}
} = {}) => async (dispatch, getState) => {
    const accountId = get(getState(), 'user.account.id');
    const requestOptions = {};
    if (headers) requestOptions.headers = headers;
    if (options.timeout) requestOptions.timeout = options.timeout;
    if (baseUrl) requestOptions.baseUrl = baseUrl;

    dispatch(setFetchGroupsError(null));
    dispatch(setIsFetchingGroups(true));

    try {
        const groups = await groupApi.getAllGroups({
            accountId,
            ...requestOptions
        });
        dispatch(setGroups(groups));
        return true;
    } catch (err) {
        dispatch(setFetchGroupsError(getErrorMessage(err)));
        return false;
    } finally {
        dispatch(setIsFetchingGroups(false));
    }
};

/**
 * Populates groupMembersByGroupId: undefined = not fetched, null = lookup failed, array = fetched members.
 */
export const fetchGroupMembersForRecipients = (recipients = []) => async (dispatch, getState) => {
    const state = getState();
    const userAccountId = get(state, 'user.account.id');
    const cachedMembersByGroupId = state.recipients.groupMembersByGroupId || {};
    const inFlightIds = new Set(state.recipients.inFlightGroupMemberIds || []);

    const seenGroupIds = new Set();
    const lookups = [];
    recipients.forEach((recipient) => {
        const groupId = recipient?.group_id;
        if (!groupId || seenGroupIds.has(groupId)) return;
        seenGroupIds.add(groupId);
        if (cachedMembersByGroupId[groupId] !== undefined) return;
        if (inFlightIds.has(groupId)) return;
        const accountId = recipient.account_id || userAccountId;
        if (!accountId) return;
        lookups.push({accountId, groupId});
    });
    if (lookups.length === 0) return;

    const lookupGroupIds = lookups.map(({groupId}) => groupId);
    dispatch(addInFlightGroupMemberIds(lookupGroupIds));

    try {
        const responses = await Promise.all(
            lookups.map(({accountId, groupId}) => groupApi
                .getGroupMembers({accountId, groupId})
                .catch(() => null))
        );

        const membersByGroupId = {};
        lookups.forEach(({groupId}, index) => {
            const response = responses[index];
            if (response === null) {
                membersByGroupId[groupId] = null;
                return;
            }
            const candidates = [
                response?.data?.data,
                response?.data,
                response
            ];
            const members = candidates.find((value) => Array.isArray(value));
            membersByGroupId[groupId] = Array.isArray(members) ? members : [];
        });

        dispatch(setGroupMembersByGroupId(membersByGroupId));
    } finally {
        dispatch(removeInFlightGroupMemberIds(lookupGroupIds));
    }
};

function getArrayFromResponse(response) {
    const candidates = [response?.data?.data, response?.data, response];
    return candidates.find((value) => Array.isArray(value)) || [];
}

/**
 * Resolves the current user's group membership per account; an account key stays absent while unresolved so callers can fail open and retry.
 */
export const fetchCurrentUserGroupIds = (recipients = []) => async (dispatch, getState) => {
    const state = getState();
    const resolvedByAccountId = state.recipients.currentUserGroupIdsByAccountId || {};
    const inFlightAccountIds = new Set(state.recipients.inFlightGroupMembershipAccountIds || []);
    const userAccountId = get(state, 'user.account.id');

    const requestedAccountIds = new Set();
    recipients.forEach((recipient) => {
        if (!recipient?.group_id) return;
        const accountId = recipient.account_id || userAccountId;
        if (!accountId) return;
        if (resolvedByAccountId[accountId] || inFlightAccountIds.has(accountId)) return;
        requestedAccountIds.add(accountId);
    });
    if (requestedAccountIds.size === 0) {
        return;
    }

    const userId = parseJWT(get(state, 'user.accessToken'))?.external_id || get(state, 'user.user.id');
    if (!userId) {
        return;
    }

    const lookupAccountIds = Array.from(requestedAccountIds);
    dispatch(addInFlightGroupMembershipAccountIds(lookupAccountIds));

    try {
        const accessibleAccountsResponse = await accountUserApi.getUserAccessibleAccounts({
            userId,
            params: {status: 'active'}
        });
        const accountUserIdByAccountId = {};
        getArrayFromResponse(accessibleAccountsResponse).forEach((accountUser) => {
            if (accountUser?.account_id && accountUser?.id) {
                accountUserIdByAccountId[accountUser.account_id] = accountUser.id;
            }
        });

        const membershipResults = await Promise.all(lookupAccountIds.map(async (accountId) => {
            const accountUserId = accountUserIdByAccountId[accountId];
            if (!accountUserId) return {accountId, groupIds: []};

            const response = await groupApi.getAccountUserMemberships({accountUserId});
            const groupIds = getArrayFromResponse(response)
                .map((membership) => membership?.group?.id)
                .filter(Boolean);
            return {accountId, groupIds};
        }));

        const groupIdsByAccountId = {};
        membershipResults.forEach(({accountId, groupIds}) => {
            groupIdsByAccountId[accountId] = groupIds;
        });

        dispatch(setCurrentUserGroupIdsByAccountId(groupIdsByAccountId));
    } catch (err) {
        console.error('fetchCurrentUserGroupIds failed', err);
    } finally {
        dispatch(removeInFlightGroupMembershipAccountIds(lookupAccountIds));
    }
};

export const fetchVerificationTypes = ({
    headers, baseUrl, options = {}
} = {}) => async (dispatch, getState) => {
    const accountId = get(getState(), 'user.account.id');
    const requestOptions = {};
    if (headers) requestOptions.headers = headers;
    if (options.timeout) requestOptions.timeout = options.timeout;
    if (baseUrl) requestOptions.baseUrl = baseUrl;

    dispatch(setIsVerificationTypesFetching(true));
    dispatch(setVerificationTypesFetchError(null));
    try {
        const result = await esvApi.getAccountProviders({accountId, ...requestOptions});
        const verificationTypes = Array.isArray(result.data) ? result.data : get(result, 'data.data', []);
        dispatch(setVerificationTypes(verificationTypes));
        return true;
    } catch (err) {
        dispatch(setVerificationTypesFetchError(getErrorMessage(err)));
        return false;
    } finally {
        dispatch(setIsVerificationTypesFetching(false));
    }
};

export const fetchRecipientVerification = ({
    recipientId, packageData
} = {}) => async (dispatch) => {
    dispatch(addFetchingVerificationRecipientsIds(recipientId));

    try {
        const result = await esvApi.getVerification({
            transactionId: packageData.id,
            recipientId
        });
        dispatch(setVerifications({
            packageId: packageData.id,
            recipientId,
            verification: get(result, 'data', {})
        }));
        dispatch(removeFetchingVerificationRecipientsIds(recipientId));
        return get(result, 'data', {});
    } catch (err) {
        dispatch(removeFetchingVerificationRecipientsIds(recipientId));
        return null;
    }
};

export const upsertVerification = ({
    recipientId, typeId, packageType
} = {}) => async (dispatch, getState) => {
    const packageData = get(getState(), getPackageStatePath(packageType));

    dispatch(setIsVerificationTypeSaving(true));
    try {
        await esvApi.createOrUpdateVerification({
            transactionId: packageData.id,
            recipientId,
            data: {account_provider_id: typeId}
        });
        dispatch(setIsVerificationTypeSaving(false));
        dispatch(fetchRecipientVerification({recipientId, packageData}));
        return true;
    } catch (err) {
        dispatch(setIsVerificationTypeSaving(false));
        return false;
    }
};

export const removeVerification = ({
    recipientId, packageType
} = {}) => async (dispatch, getState) => {
    const packageData = get(getState(), getPackageStatePath(packageType));

    dispatch(setIsVerificationTypeSaving(true));
    try {
        await esvApi.deleteVerification({
            transactionId: packageData.id,
            recipientId
        });
        dispatch(setIsVerificationTypeSaving(false));
        dispatch(setVerifications({packageId: packageData.id, recipientId, verification: null}));
        return true;
    } catch (err) {
        dispatch(setIsVerificationTypeSaving(false));
        return false;
    }
};

export const fetchAvatarsByEmails = ({
    baseUrl, headers, emails = [], options = {}
} = {}) => async () => false;

export const selectSuggestions = (state) => state.recipients.suggestions;
export const selectUpdatedSuggestions = (state) => state.recipients.updatedSuggestions;
export const selectMergedSuggestionByEmail = (state, email) => {
    const results = state.recipients?.updatedSuggestions?.editRecipient?.results || [];
    const normalizedEmail = getNormalizedEmail(email);
    if (!normalizedEmail) return null;
    return results.find((suggestion) => getNormalizedEmail(suggestion.email) === normalizedEmail) || null;
};
export const selectIsFetchingSuggestions = (state) => state.recipients.isFetchingSuggestions;
export const selectFetchSuggestionsError = (state) => state.recipients.fetchSuggestionsError;

export const selectIsUpdatingRoles = (state) => state.recipients.isUpdatingRoles;
export const selectUpdateRolesError = (state) => state.recipients.updateRolesError;

export const selectsUpdatingFlow = (state) => state.recipients.isUpdatingFlow;
export const selectupdateFlowError = (state) => state.recipients.updateFlowError;

export const selectIsUpdatingRole = (state) => state.recipients.isUpdatingRole;
export const selectUpdateRoleError = (state) => state.recipients.updateRoleError;

export const selectIsDeletingRole = (state) => state.recipients.isDeletingRole;
export const selectDeleteRoleError = (state) => state.recipients.deleteRoleError;

export const selectGroupsSummary = (state) => state.recipients.groups;
export const selectIsFetchingGroups = (state) => state.recipients.isFetchingGroups;
export const selectFetchGroupsError = (state) => state.recipients.fetchGroupsError;
export const selectGroupMembersByGroupId = (state) => state.recipients.groupMembersByGroupId;

export const selectCurrentUserGroupIdsByAccountId = (state) => state.recipients.currentUserGroupIdsByAccountId;

export const selectVerificationTypes = (state) => state.recipients.verificationTypes;
export const selectVerifications = (state) => state.recipients.verifications;
export const selectIsVerificationTypesFetching = (state) => state.recipients.isVerificationTypesFetching;
export const selectVerificationTypesFetchError = (state) => state.recipients.verificationTypesFetchError;

export const selectIsVerificationTypeFetching = (state) => state.recipients.isVerificationTypeFetching;
export const selectVerificationTypeFetchError = (state) => state.recipients.verificationTypeFetchError;

export const selectIsVerificationTypeSaving = (state) => state.recipients.isVerificationTypeSaving;
export const selectVerificationTypeSaveError = (state) => state.recipients.verificationTypeSaveError;

export const selectUpdateSettingsError = (state) => state.recipients.updateSettingsError;
export const selectAvatars = (state) => state.recipients.avatars;

export const selectIsRecipientVerificationFetching = (recipientId) => (state) => state
    .recipients.fetchingVerificationRecipientsIds.includes(recipientId);

export default recipientsSlice.reducer;
